import { EncryptConstants } from '@constants/encryptionConstants';

const ENCRYPTION_KEY_STORAGE_KEY = 'encryption_key';

const generateEncryptionKey = async (): Promise<CryptoKey> => {
  return await window.crypto.subtle.generateKey(
    {
      name: EncryptConstants.AES_GCM,
      length: EncryptConstants.LENGTH,
    } as AesKeyGenParams,
    true,
    [EncryptConstants.ENCRYPT, EncryptConstants.DECRYPT] as KeyUsage[]
  );
};

const exportKey = async (key: CryptoKey): Promise<string> => {
  const exported = await window.crypto.subtle.exportKey('raw', key);
  return btoa(String.fromCharCode(...new Uint8Array(exported)));
};

const importKey = async (keyStr: string): Promise<CryptoKey> => {
  const keyData = Uint8Array.from(atob(keyStr), c => c.charCodeAt(0));
  return await window.crypto.subtle.importKey(
    'raw',
    keyData,
    {
      name: EncryptConstants.AES_GCM,
      length: EncryptConstants.LENGTH,
    } as AesKeyAlgorithm,
    true,
    [EncryptConstants.ENCRYPT, EncryptConstants.DECRYPT] as KeyUsage[]
  );
};

const getOrCreateEncryptionKey = async (): Promise<CryptoKey> => {
  const storedKey = localStorage.getItem(ENCRYPTION_KEY_STORAGE_KEY);
  if (storedKey) {
    return await importKey(storedKey);
  }
  const newKey = await generateEncryptionKey();
  const exportedKey = await exportKey(newKey);
  localStorage.setItem(ENCRYPTION_KEY_STORAGE_KEY, exportedKey);
  return newKey;
};

export const encrypt = async (data: string): Promise<string> => {
  const key = await getOrCreateEncryptionKey();
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(data);

  const encrypted = await window.crypto.subtle.encrypt(
    {
      name: EncryptConstants.AES_GCM,
      iv,
    },
    key,
    encoded
  );

  const encryptedArray = new Uint8Array(encrypted);
  const combined = new Uint8Array(iv.length + encryptedArray.length);
  combined.set(iv);
  combined.set(encryptedArray, iv.length);

  return btoa(String.fromCharCode(...combined));
};

export const decrypt = async (encryptedData: string): Promise<string> => {
  const key = await getOrCreateEncryptionKey();
  const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

  const iv = combined.slice(0, 12);
  const encrypted = combined.slice(12);

  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: EncryptConstants.AES_GCM,
      iv,
    },
    key,
    encrypted
  );

  return new TextDecoder().decode(decrypted);
};
